import { ApiError, unwrap, type Schemas } from '@mrigl/api-client';
import type { MessageKey } from '@mrigl/i18n';
import { Alert, Button, SelectField, TextField } from '@mrigl/ui';
import { useCallback, useEffect, useId, useState, type SubmitEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, errorMessage } from '../../api.js';
import { Loading } from '../../components.js';
import {
  GridBody,
  GridToolbar,
  Pagination,
  SearchFilter,
  SortHeader,
  useGridState,
  usePagedData,
  useRowLink,
  type ListQuery,
} from '../../grid.js';
import { useI18n, type I18n } from '../../i18n.js';
import { display, useStore, type StoreDetails } from '../catalog/shared.js';

type Item = Schemas['InventoryItem'];
type Level = Schemas['StockLevel'];
type Movement = Schemas['InventoryMovementList']['items'][number];
type Reason = Schemas['InventoryAdjustment']['reason'];

const REASONS: readonly Reason[] = ['received', 'returned', 'damaged', 'correction'];

function itemName(item: Item, locale: I18n['locale'], store: StoreDetails): string {
  const title = display(item.title, locale, store, item.handle);
  const options = item.optionLabels.map((label) => display(label, locale, store)).filter(Boolean);
  return options.length === 0 ? title : `${title} — ${options.join(' / ')}`;
}

function stateKey(level: Level): MessageKey {
  if (!level.tracked) return 'admin.inventory.state.untracked';
  if (level.allowBackorder) return 'admin.inventory.state.backorder';
  return level.sellable ? 'admin.inventory.state.in' : 'admin.inventory.state.out';
}

function inventoryError(t: I18n['t'], failure: unknown): string {
  if (failure instanceof ApiError && failure.code === 'stock-conflict') {
    return t('admin.inventory.error.stock-conflict');
  }
  return errorMessage(t, failure);
}

/** A fresh key per intended change; kept for retries of the same submission. */
function newKey(): string {
  return `admin-${globalThis.crypto.randomUUID()}`;
}

type InventoryQuery = ListQuery<'/admin-api/v1/stores/{storeId}/inventory'>;

/** Stock of every variant of the store: search, state filter, sortable columns, pages. */
export function InventoryPage() {
  const { storeId = '' } = useParams();
  const { t, locale } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const grid = useGridState({
    sort: 'product',
    sortable: ['product', 'sku', 'onHand', 'available'],
    filters: { q: '', filter: '' },
  });
  const rowLink = useRowLink();
  const { filters } = grid;
  const key = JSON.stringify([storeId, locale, grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error } = usePagedData<Item>(
    key,
    () =>
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/inventory', {
          params: {
            path: { storeId },
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as NonNullable<InventoryQuery['sort']>,
              locale,
              ...(filters.q === '' ? {} : { q: filters.q }),
              ...(filters.filter === ''
                ? {}
                : { filter: filters.filter as NonNullable<InventoryQuery['filter']> }),
            },
          },
        }),
      ),
    grid.setPage,
  );

  if (storeError !== null) return <Alert tone="error">{storeError}</Alert>;
  if (store === null) return <Loading />;
  const header = (field: string, label: string, firstDescending = false, numeric = false) => (
    <SortHeader
      field={field}
      label={label}
      sort={grid.sort}
      onSort={grid.setSort}
      firstDescending={firstDescending}
      numeric={numeric}
    />
  );

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}`}>{display(store.name, locale, store, store.slug)}</Link>
      </p>
      <h1 className="mrigl-heading">{t('admin.inventory.title')}</h1>
      <GridToolbar
        activeFilters={grid.activeFilters}
        onReset={grid.reset}
        search={
          <SearchFilter
            label={t('admin.inventory.search')}
            value={filters.q}
            onChange={(value) => {
              grid.setFilter('q', value);
            }}
          />
        }
      >
        <SelectField
          label={t('admin.inventory.filter')}
          value={filters.filter}
          onChange={(event) => {
            grid.setFilter('filter', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.inventory.filter.all') },
            { value: 'tracked', label: t('admin.inventory.filter.tracked') },
            { value: 'untracked', label: t('admin.inventory.filter.untracked') },
            { value: 'out-of-stock', label: t('admin.inventory.filter.out-of-stock') },
          ]}
        />
      </GridToolbar>
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={t('admin.inventory.empty')}
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  {header('product', t('admin.inventory.column.item'))}
                  {header('sku', t('admin.product.sku'))}
                  {header('onHand', t('admin.inventory.column.onHand'), true, true)}
                  <th scope="col" className="admin-num">
                    {t('admin.inventory.column.reserved')}
                  </th>
                  {header('available', t('admin.inventory.column.available'), true, true)}
                  <th scope="col">{t('admin.inventory.column.state')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr
                    key={item.variantId}
                    {...rowLink(`/stores/${storeId}/inventory/${item.variantId}`)}
                  >
                    <td>
                      <Link
                        className="admin-cell-link"
                        to={`/stores/${storeId}/inventory/${item.variantId}`}
                        aria-label={t('admin.inventory.manage', {
                          name: itemName(item, locale, store),
                        })}
                      >
                        {itemName(item, locale, store)}
                      </Link>
                    </td>
                    <td className="mrigl-ltr">{item.sku ?? '—'}</td>
                    <td className="admin-num">{item.tracked ? item.onHand : '—'}</td>
                    <td className="admin-num">{item.tracked ? item.reserved : '—'}</td>
                    <td className="admin-num admin-cell-strong">
                      {item.tracked ? item.available : '—'}
                    </td>
                    <td>
                      <span className={`admin-status admin-status--stock-${stockTone(item)}`}>
                        {t(stateKey(item))}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={grid.page}
            pageSize={grid.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
    </div>
  );
}

/** Badge colour of a stock state. */
function stockTone(level: Level): 'none' | 'ok' | 'out' {
  if (!level.tracked) return 'none';
  return level.sellable ? 'ok' : 'out';
}

/** Stock of one variant: tracking settings, adjustment, count, history. */
export function VariantStockPage() {
  const { storeId = '', variantId = '' } = useParams();
  const { t, locale, formatDateTime } = useI18n();
  const { store, error: storeError } = useStore(storeId, t);
  const [item, setItem] = useState<Item | null>(null);
  const [history, setHistory] = useState<Movement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    const page = await unwrap(
      api.GET('/admin-api/v1/stores/{storeId}/inventory/{variantId}/movements', {
        params: { path: { storeId, variantId }, query: { limit: 50 } },
      }),
    );
    setHistory(page.items);
  }, [storeId, variantId]);

  useEffect(() => {
    void Promise.all([
      unwrap(
        api.GET('/admin-api/v1/stores/{storeId}/inventory/{variantId}', {
          params: { path: { storeId, variantId } },
        }),
      ).then(setItem),
      loadHistory(),
    ]).catch((failure: unknown) => {
      setError(errorMessage(t, failure));
    });
  }, [storeId, variantId, loadHistory, t]);

  const onChanged = (level: Level, message: string) => {
    setItem((current) => (current === null ? current : { ...current, ...level }));
    setNotice(message);
    void loadHistory().catch(() => undefined);
  };

  const failure = storeError ?? error;
  if (failure !== null) return <Alert tone="error">{failure}</Alert>;
  if (store === null || item === null) return <Loading />;
  const canManage = store.permissions.includes('inventory.manage');

  return (
    <div className="mrigl-stack">
      <p>
        <Link to={`/stores/${storeId}/inventory`}>{t('admin.inventory.title')}</Link>
      </p>
      <h1 className="mrigl-heading">{itemName(item, locale, store)}</h1>
      <dl className="admin-stats">
        <div>
          <dt>{t('admin.inventory.column.onHand')}</dt>
          <dd>{item.onHand}</dd>
        </div>
        <div>
          <dt>{t('admin.inventory.column.reserved')}</dt>
          <dd>{item.reserved}</dd>
        </div>
        <div>
          <dt>{t('admin.inventory.column.available')}</dt>
          <dd>{item.available}</dd>
        </div>
        <div>
          <dt>{t('admin.inventory.column.state')}</dt>
          <dd>{t(stateKey(item))}</dd>
        </div>
      </dl>
      <div role="status">{notice === null ? null : <Alert tone="success">{notice}</Alert>}</div>
      {canManage ? (
        <>
          <SettingsForm
            store={store}
            level={item}
            onSaved={(level) => {
              onChanged(level, t('admin.inventory.settingsSaved'));
            }}
          />
          <AdjustForm
            store={store}
            variantId={item.variantId}
            onSaved={(level) => {
              onChanged(level, t('admin.inventory.saved'));
            }}
          />
          <CountForm
            store={store}
            variantId={item.variantId}
            onSaved={(level) => {
              onChanged(level, t('admin.inventory.saved'));
            }}
          />
        </>
      ) : null}
      <section className="mrigl-card mrigl-stack" aria-labelledby="history-title">
        <h2 id="history-title" className="mrigl-subheading">
          {t('admin.inventory.history')}
        </h2>
        {history === null ? (
          <Loading />
        ) : history.length === 0 ? (
          <p className="mrigl-muted">{t('admin.inventory.noHistory')}</p>
        ) : (
          <div className="admin-table-wrap">
            <table className="mrigl-table">
              <thead>
                <tr>
                  <th scope="col">{t('admin.inventory.column.date')}</th>
                  <th scope="col">{t('admin.inventory.reason')}</th>
                  <th scope="col">{t('admin.inventory.column.change')}</th>
                  <th scope="col">{t('admin.inventory.column.after')}</th>
                  <th scope="col">{t('admin.inventory.column.note')}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((movement) => (
                  <tr key={movement.movementId}>
                    <td>{formatDateTime(movement.createdAt)}</td>
                    <td>{t(`admin.inventory.reason.${movement.reason}` as MessageKey)}</td>
                    <td className="mrigl-ltr">
                      {movement.onHandDelta === 0
                        ? movement.reservedDelta === 0
                          ? '—'
                          : `${movement.reservedDelta > 0 ? '+' : ''}${String(movement.reservedDelta)} (${t('admin.inventory.column.reserved')})`
                        : `${movement.onHandDelta > 0 ? '+' : ''}${String(movement.onHandDelta)}`}
                    </td>
                    <td>{movement.onHandAfter}</td>
                    <td>{movement.note ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="admin-checkbox">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
      />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

function SettingsForm({
  store,
  level,
  onSaved,
}: {
  readonly store: StoreDetails;
  readonly level: Level;
  readonly onSaved: (level: Level) => void;
}) {
  const { t } = useI18n();
  const [tracked, setTracked] = useState(level.tracked);
  const [allowBackorder, setAllowBackorder] = useState(level.allowBackorder);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onSaved(
        await unwrap(
          api.PUT('/admin-api/v1/stores/{storeId}/inventory/{variantId}/settings', {
            params: { path: { storeId: store.storeId, variantId: level.variantId } },
            body: { tracked, allowBackorder },
          }),
        ),
      );
    } catch (failure) {
      setError(inventoryError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-card mrigl-stack"
      aria-label={t('admin.inventory.settings')}
      onSubmit={(event) => void save(event)}
    >
      <h2 className="mrigl-subheading">{t('admin.inventory.settings')}</h2>
      <Checkbox label={t('admin.inventory.track')} checked={tracked} onChange={setTracked} />
      <Checkbox
        label={t('admin.inventory.allowBackorder')}
        checked={allowBackorder}
        onChange={setAllowBackorder}
      />
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <Button type="submit" busy={busy}>
        {t('admin.inventory.saveSettings')}
      </Button>
    </form>
  );
}

function AdjustForm({
  store,
  variantId,
  onSaved,
}: {
  readonly store: StoreDetails;
  readonly variantId: string;
  readonly onSaved: (level: Level) => void;
}) {
  const { t } = useI18n();
  const [reason, setReason] = useState<Reason>('received');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [key, setKey] = useState(newKey);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Any edit means a new intended change, hence a new key; a plain retry keeps it.
  const edit =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setKey(newKey());
    };

  async function apply(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const amount = Number(quantity);
    const signed =
      reason === 'correction'
        ? /^-?[0-9]+$/.test(quantity.trim())
        : /^[0-9]+$/.test(quantity.trim());
    if (!signed || amount === 0) {
      setError(t('admin.inventory.error.quantity'));
      return;
    }
    setBusy(true);
    try {
      const level = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/inventory/{variantId}/adjustments', {
          params: {
            path: { storeId: store.storeId, variantId },
            header: { 'idempotency-key': key },
          },
          body: {
            delta: reason === 'damaged' ? -amount : amount,
            reason,
            note: note.trim() === '' ? null : note.trim(),
          },
        }),
      );
      onSaved(level);
      setQuantity('');
      setNote('');
      setKey(newKey());
    } catch (failure) {
      setError(inventoryError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-card mrigl-stack"
      aria-label={t('admin.inventory.adjust')}
      onSubmit={(event) => void apply(event)}
    >
      <h2 className="mrigl-subheading">{t('admin.inventory.adjust')}</h2>
      <SelectField
        label={t('admin.inventory.reason')}
        value={reason}
        onChange={(event) => {
          edit(setReason)(event.target.value as Reason);
        }}
        options={REASONS.map((value) => ({ value, label: t(`admin.inventory.reason.${value}`) }))}
      />
      <TextField
        ltr
        inputMode="numeric"
        required
        label={t('admin.inventory.quantity')}
        hint={t(
          reason === 'correction'
            ? 'admin.inventory.correctionHint'
            : 'admin.inventory.quantityHint',
        )}
        value={quantity}
        onChange={(event) => {
          edit(setQuantity)(event.target.value);
        }}
      />
      <TextField
        label={t('admin.inventory.note')}
        maxLength={500}
        value={note}
        onChange={(event) => {
          edit(setNote)(event.target.value);
        }}
      />
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <Button type="submit" busy={busy}>
        {t('admin.inventory.apply')}
      </Button>
    </form>
  );
}

function CountForm({
  store,
  variantId,
  onSaved,
}: {
  readonly store: StoreDetails;
  readonly variantId: string;
  readonly onSaved: (level: Level) => void;
}) {
  const { t } = useI18n();
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [key, setKey] = useState(newKey);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!/^[0-9]+$/.test(counted.trim())) {
      setError(t('admin.inventory.error.quantity'));
      return;
    }
    setBusy(true);
    try {
      const level = await unwrap(
        api.POST('/admin-api/v1/stores/{storeId}/inventory/{variantId}/counts', {
          params: {
            path: { storeId: store.storeId, variantId },
            header: { 'idempotency-key': key },
          },
          body: { onHand: Number(counted), note: note.trim() === '' ? null : note.trim() },
        }),
      );
      onSaved(level);
      setCounted('');
      setNote('');
      setKey(newKey());
    } catch (failure) {
      setError(inventoryError(t, failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mrigl-card mrigl-stack"
      aria-label={t('admin.inventory.count')}
      onSubmit={(event) => void save(event)}
    >
      <h2 className="mrigl-subheading">{t('admin.inventory.count')}</h2>
      <TextField
        ltr
        inputMode="numeric"
        required
        label={t('admin.inventory.counted')}
        value={counted}
        onChange={(event) => {
          setCounted(event.target.value);
          setKey(newKey());
        }}
      />
      <TextField
        label={t('admin.inventory.note')}
        maxLength={500}
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
          setKey(newKey());
        }}
      />
      {error === null ? null : <Alert tone="error">{error}</Alert>}
      <Button type="submit" busy={busy}>
        {t('admin.inventory.saveCount')}
      </Button>
    </form>
  );
}
